import * as actionTypes from './action-types'
import * as mutationTypes from './mutation-types'
import {
  fetchAgencyAgents,
  fetchAgencyClients,
  fetchAgencySites,
  getAgencyConfig,
  getAgencyPricing
} from '@/utils/api/agency'
import { deleteAgencyAgent, postAgencyAgent } from '@/utils/api/agency/agents'
import { deleteAgencyCustomer, postAgencyCustomer } from '@/utils/api/agency/customers'
import { deleteAgencyCustomerSite, postAgencyCustomerSite } from '@/utils/api/agency/customer/sites'
import { deleteAgencyCustomerRound, fetchAgencyRondes, postAgencyRound } from '@/utils/api/agency/rondes'
import { deleteCheckpoint, fetchAgencyCheckpoints } from '@/utils/api/agency/checkpoints'
import { postAgencyCustomerSiteCheckpoint } from '@/utils/api/agency/customer/site/checkpoints'
import {
  ACCOUNT_RIGHT_CREATE_ROUND,
  ACCOUNT_RIGHT_LIST_AGENTS,
  ACCOUNT_RIGHT_LIST_CHECKPOINTS,
  ACCOUNT_RIGHT_LIST_CUSTOMERS,
  ACCOUNT_RIGHT_LIST_MISSIONS_ROUNDS,
  ACCOUNT_RIGHT_LIST_ROUNDS,
  ACCOUNT_RIGHT_LIST_SITES
} from '@/consts/rights'
import { fetchAgencies, getAdminAgency, postAdminAgency } from '@/utils/api/admin'
import { SET_CONFIG } from '@/store/mutation-types'
import { getCircuitRounds } from '@/utils/api/agency/circuits'
import { getAgencyAgentRequestUnread } from '@/utils/api/agency/agent/request'

export default {
  // TODO simplifier en une seule requète ?
  [actionTypes.USER_AGENCY_FETCH_INFOS]: async ({ dispatch, commit, rootGetters, rootState }) => {
    dispatch(actionTypes.USER_AGENCY_FETCH_CONFIG)
    dispatch(actionTypes.USER_AGENCY_GET_AGENT_REQUEST_UNREAD)
    if (rootGetters['user/isAdmin']) {
      await Promise.all([
        dispatch(actionTypes.USER_AGENCY_FETCH_AGENCIES)
      ])
      if (rootState.user.alteredAgency) {
        const { agency } = await getAdminAgency(rootState.user.alteredAgency)
        commit(mutationTypes.USER_AGENCY_SET_AGENCY, agency)
      }
    }
    if (rootGetters['user/agencySlug']) { // TODO droits des clients
      await Promise.all([
        dispatch(actionTypes.USER_AGENCY_FETCH_SITES),
        dispatch(actionTypes.USER_AGENCY_FETCH_AGENTS),
        dispatch(actionTypes.USER_AGENCY_FETCH_CLIENTS),
        dispatch(actionTypes.USER_AGENCY_GET_PRICING),
        dispatch(actionTypes.USER_AGENCY_FETCH_RONDES),
        dispatch(actionTypes.USER_AGENCY_FETCH_SITE_RONDES),
        dispatch(actionTypes.USER_AGENCY_FETCH_CHECKPOINTS)
      ])
    }
  },
  [actionTypes.USER_AGENCY_GET_AGENT_REQUEST_UNREAD]: async ({ commit, rootGetters }) => {
    if (rootGetters['user/isCustomer']) return
    const agentRequestViews = await getAgencyAgentRequestUnread()
    commit(mutationTypes.USER_AGENCY_AGENT_REQUEST_UNREAD, agentRequestViews)
  },
  [actionTypes.USER_AGENCY_GET_PRICING]: async ({ commit, rootGetters }) => {
    if (rootGetters['user/isCustomer']) return
    const pricing = await getAgencyPricing()
    commit(mutationTypes.USER_AGENCY_SET_PRICING, pricing)
  },
  // Agences
  [actionTypes.USER_AGENCY_FETCH_AGENCIES]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isAdmin']) return
    commit(mutationTypes.USER_AGENCY_SET_FETCHING, { agencies: true })
    const { companies } = await fetchAgencies()
    commit(mutationTypes.USER_AGENCY_SET_AGENCIES, _.sortBy(companies, 'name'))
  },
  [actionTypes.USER_AGENCY_POST_AGENCY]: async ({ commit }, _agency) => {
    const { agency } = await postAdminAgency(_agency)
    commit(mutationTypes.USER_AGENCY_SET_AGENCY, agency)
  },
  // Config
  [actionTypes.USER_AGENCY_FETCH_CONFIG]: async ({ commit }) => {
    const config = await getAgencyConfig()
    commit(SET_CONFIG, config, { root: true })
  },
  // Sites
  [actionTypes.USER_AGENCY_FETCH_SITES]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_LIST_SITES])) return
    commit(mutationTypes.USER_AGENCY_SET_FETCHING, { sites: true })
    const { sites } = await fetchAgencySites()
    commit(mutationTypes.USER_AGENCY_SET_SITES, _.sortBy(sites, 'name'))
  },
  [actionTypes.USER_AGENCY_POST_SITE]: async ({ commit, getters }, _site) => {
    const data = await postAgencyCustomerSite(_site)
    const { site: { checkpoints = [], ...site } } = data
    commit(mutationTypes.USER_AGENCY_SET_SITE, site)
    checkpoints.forEach((c) => {
      const storeCheckpoint = getters.getCheckpointId(c.id)
      if (storeCheckpoint && !_.isMatch(storeCheckpoint, c)) {
        commit(mutationTypes.USER_AGENCY_SET_CHECKPOINT, { ...storeCheckpoint, ...c })
      }
    })
    return site
  },
  [actionTypes.USER_AGENCY_DELETE_SITE]: async ({ commit, dispatch }, _site) => {
    await deleteAgencyCustomerSite(_site)
    commit(mutationTypes.USER_AGENCY_DELETE_SITE, _site)
    await Promise.all([
      dispatch(actionTypes.USER_AGENCY_FETCH_RONDES),
      dispatch(actionTypes.USER_AGENCY_FETCH_CHECKPOINTS)
    ])
  },
  // Agents
  [actionTypes.USER_AGENCY_FETCH_AGENTS]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_LIST_AGENTS])) return
    commit(mutationTypes.USER_AGENCY_SET_FETCHING, { agents: true })
    const result = await fetchAgencyAgents({ date: new Date() })
    const agents = _.sortBy(result.data, ['lastname', 'firstname'])
    commit(mutationTypes.USER_AGENCY_SET_AGENTS, agents)
  },
  [actionTypes.USER_AGENCY_POST_AGENT]: async ({ commit, dispatch }, _agent) => {
    const { agent } = await postAgencyAgent(_agent)
    commit(mutationTypes.USER_AGENCY_SET_AGENT, agent)
    if (!_agent.id || agent.enabled !== _agent.enabled) {
      dispatch(actionTypes.USER_AGENCY_GET_PRICING)
    }
  },
  [actionTypes.USER_AGENCY_DELETE_AGENT]: async ({ commit, dispatch }, _agent) => {
    await deleteAgencyAgent(_agent)
    commit(mutationTypes.USER_AGENCY_DELETE_AGENT, _agent)
    await Promise.all([
      dispatch(actionTypes.USER_AGENCY_FETCH_AGENTS)
    ])
  },
  // Clients
  [actionTypes.USER_AGENCY_FETCH_CLIENTS]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_LIST_CUSTOMERS])) return
    commit(mutationTypes.USER_AGENCY_SET_FETCHING, { clients: true })
    const clients = _.sortBy(await fetchAgencyClients({
      with: {
        leftToPay: true
      }
    }), 'name')
    commit(mutationTypes.USER_AGENCY_SET_CLIENTS, clients)
  },
  [actionTypes.USER_AGENCY_POST_CLIENT]: async ({ commit }, _customer) => {
    const { customer } = await postAgencyCustomer(_customer)
    commit(mutationTypes.USER_AGENCY_SET_CLIENT, customer)
  },
  [actionTypes.USER_AGENCY_DELETE_CLIENT]: async ({ commit, dispatch }, _customer) => {
    await deleteAgencyCustomer(_customer)
    commit(mutationTypes.USER_AGENCY_DELETE_CLIENT, _customer)
    await Promise.all([
      dispatch(actionTypes.USER_AGENCY_FETCH_SITES),
      dispatch(actionTypes.USER_AGENCY_FETCH_RONDES),
      dispatch(actionTypes.USER_AGENCY_FETCH_CHECKPOINTS)
    ])
  },
  // Rondes
  [actionTypes.USER_AGENCY_FETCH_RONDES]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_LIST_ROUNDS])) return
    commit(mutationTypes.USER_AGENCY_SET_FETCHING, { rondes: true })
    const { data } = await fetchAgencyRondes({})
    commit(mutationTypes.USER_AGENCY_SET_RONDES, data)
  },
  [actionTypes.USER_AGENCY_FETCH_SITE_RONDES]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_LIST_MISSIONS_ROUNDS])) return
    if (!rootGetters['service/serviceActif']) return
    const { rounds = [] } = await getCircuitRounds(rootGetters['service/serviceActif'])
    rounds.forEach((round) => {
      commit(mutationTypes.USER_AGENCY_SET_RONDE, round)
    })
  },
  [actionTypes.USER_AGENCY_POST_RONDE]: async ({ commit, rootGetters }, _ronde) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_CREATE_ROUND])) return
    const { round } = await postAgencyRound(_ronde)
    commit(mutationTypes.USER_AGENCY_SET_RONDE, round)
  },
  [actionTypes.USER_AGENCY_DELETE_RONDE]: async ({ commit }, _ronde) => {
    await deleteAgencyCustomerRound({
      client: _ronde.site.customer.slug,
      site: _ronde.site.slug,
      slug: _ronde.slug
    })
    commit(mutationTypes.USER_AGENCY_DELETE_RONDE, _ronde)
  },
  // Checkpoints
  [actionTypes.USER_AGENCY_FETCH_CHECKPOINTS]: async ({ commit, rootGetters }) => {
    if (!rootGetters['user/isGranted']([ACCOUNT_RIGHT_LIST_CHECKPOINTS])) return
    if (rootGetters['user/isCustomer']) return // TODO check rights
    commit(mutationTypes.USER_AGENCY_SET_FETCHING, { checkpoints: true })
    const data = await fetchAgencyCheckpoints()
    commit(mutationTypes.USER_AGENCY_SET_CHECKPOINTS, data)
  },
  [actionTypes.USER_AGENCY_POST_CHECKPOINT]: async ({ commit }, _checkpoint) => {
    const { checkpoint } = await postAgencyCustomerSiteCheckpoint(_checkpoint)
    commit(mutationTypes.USER_AGENCY_SET_CHECKPOINT, checkpoint)
  },
  [actionTypes.USER_AGENCY_DELETE_CHECKPOINT]: async ({ commit, dispatch }, _checkpoint) => {
    await deleteCheckpoint(_checkpoint)
    commit(mutationTypes.USER_AGENCY_DELETE_CHECKPOINT, _checkpoint)
    await dispatch(actionTypes.USER_AGENCY_FETCH_RONDES)
  }
}
